import { CfbHttpError, CfbPlayer } from './cfb-client';

/**
 * Local preparation from the c473b735 handoff; backend source not yet available.
 * Clarified identities are strings and expiry is Unix seconds. Resolution is
 * not execution readiness; no real transport or catalog is connected.
 */
export type CfbIdentityValue = string;
export interface CfbInputReferences {
  slateId: CfbIdentityValue;
  contestId: CfbIdentityValue;
  projectionId: CfbIdentityValue;
  rulesVersion: CfbIdentityValue;
  modelVersion: CfbIdentityValue;
  scoringVersion: CfbIdentityValue;
  exportProfile: CfbIdentityValue;
}
export type CfbExecutionMode = 'SIM' | 'OPTO';
export interface CfbInputSelection extends CfbInputReferences {
  schemaVersion: 'acemind.cfb.input-selection.v1';
  sport: 'CFB';
  site: 'dk';
  format: 'classic';
  mode: CfbExecutionMode;
}
const referenceKeys = ['slateId', 'contestId', 'projectionId', 'rulesVersion',
  'modelVersion', 'scoringVersion', 'exportProfile'] as const;

export function cfbExecutionMode(mode: 'simulator' | 'optimizer'): CfbExecutionMode {
  if (mode === 'simulator') return 'SIM';
  if (mode === 'optimizer') return 'OPTO';
  throw new Error('Unsupported CFB mode.');
}

/** Pure local request preparation. No transport, registry lookup or execution. */
export function cfbResolveRequest(mode: 'simulator' | 'optimizer', references: CfbInputReferences) {
  if (Object.keys(references).some(key => !referenceKeys.includes(key as typeof referenceKeys[number]))) {
    throw new Error('Unexpected CFB input selection field.');
  }
  const selected = {} as CfbInputReferences;
  for (const key of referenceKeys) {
    const value = references[key];
    // Preserve string identities exactly; never coerce numeric catalog IDs.
    if (!(typeof value === 'string' && value.length > 0)) {
      throw new Error('Missing or invalid CFB input identity: ' + key);
    }
    selected[key] = value;
  }
  const body: CfbInputSelection = {
    schemaVersion: 'acemind.cfb.input-selection.v1', sport: 'CFB', site: 'dk',
    format: 'classic', mode: cfbExecutionMode(mode), ...selected,
  };
  return { path: '/inputs/resolve' as const, body };
}

/** Only the independently established common fields; constraints remain unwired. */
export function cfbCreateRequest(requestId: string, input: { inputId: string; inputHash: string }) {
  if (!requestId || !input.inputId || !input.inputHash) throw new Error('CFB create requires exact input identity.');
  return { requestId, inputId: input.inputId, inputHash: input.inputHash };
}

export function cfbInputFailure(error: unknown): 'execution_disabled' | 'input_stale_or_locked' | undefined {
  if (!(error instanceof CfbHttpError) || error.status !== 409) return undefined;
  const reason = error.reasonCode || error.code; // Preserve the error class; support older flat envelopes.
  if (reason === 'real_execution_disabled') return 'execution_disabled';
  if (reason === 'input_stale_or_locked') return 'input_stale_or_locked';
  return undefined;
}

export interface CfbResolvedIdentity {
  readonly inputId: string;
  readonly inputHash: string;
  readonly inputSelection: Readonly<CfbInputSelection>;
  /** Finite Unix seconds, possibly fractional. Resolution is not readiness. */
  readonly executionExpiresAt: number;
}

/** Full field names from the clarification; scoring's existing schema is not redefined here. */
export interface CfbResolveResponse extends CfbResolvedIdentity {
  schemaVersion: string;
  lockTime: string;
  site: 'dk';
  format: 'classic';
  rulesVersion: string;
  modelVersion: string;
  approvalId: string;
  slotColumns: string[];
  selectionScoring: null | { scoreVersion: 1; syntheticOnly: false; [key: string]: unknown };
  players: (CfbPlayer & { team: string; gameId: string })[];
}

/** Verify the identity envelope only. HTTP 200 is not execution readiness. */
export function cfbResolvedIdentity(response: unknown, requested: CfbInputSelection): CfbResolvedIdentity {
  if (!response || typeof response !== 'object') throw new Error('Missing CFB resolve response.');
  const value = response as Record<string, unknown>;
  const selection = value['inputSelection'];
  if (!selection || typeof selection !== 'object' ||
    Object.keys(selection).length !== Object.keys(requested).length ||
    Object.entries(requested).some(([key, item]) => (selection as Record<string, unknown>)[key] !== item)) {
    throw new Error('CFB resolved input selection mismatch.');
  }
  if (typeof value['inputId'] !== 'string' || !value['inputId'] ||
    typeof value['inputHash'] !== 'string' || !/^[0-9a-f]{64}$/.test(value['inputHash']) ||
    typeof value['executionExpiresAt'] !== 'number' || !Number.isFinite(value['executionExpiresAt'])) throw new Error('Incomplete CFB resolved identity.');
  return Object.freeze({
    inputId: value['inputId'], inputHash: value['inputHash'],
    inputSelection: Object.freeze({ ...requested }),
    executionExpiresAt: value['executionExpiresAt'],
  });
}


/** A local freshness check only; an unexpired resolve still does not authorize create. */
export function cfbExecutionExpired(input: { executionExpiresAt: number }, nowSeconds: number): boolean {
  if (!Number.isFinite(nowSeconds) || !Number.isFinite(input.executionExpiresAt)) throw new Error('Invalid CFB execution time.');
  return nowSeconds >= input.executionExpiresAt;
}

export interface CfbConstraints {
  locked_rows: number[];
  excluded_rows: number[];
  slot_locks: Record<string, number>;
  min_unique_players: number;
}

/** Pure nested constraint preparation; eligibility must come from verified rules, never position guesses. */
export function cfbConstraintEnvelope(value: unknown, players: readonly { rowIndex: number }[],
  eligibleRowsBySlot: readonly (readonly number[])[]): { constraints: CfbConstraints } {
  const rows = new Set(players.map(player => player.rowIndex));
  if (!players.length || rows.size !== players.length || players.some(player => !Number.isInteger(player.rowIndex) || player.rowIndex < 0 || player.rowIndex >= players.length) ||
    !eligibleRowsBySlot.length) throw new Error('Missing verified CFB row/slot context.');
  const source = value == null ? {} : value;
  if (typeof source !== 'object' || Array.isArray(source)) throw new Error('CFB constraints must be an object.');
  const data = source as Record<string, unknown>;
  const allowed = ['locked_rows', 'excluded_rows', 'slot_locks', 'min_unique_players'];
  if (Object.keys(data).some(key => !allowed.includes(key))) throw new Error('Unknown CFB constraint.');
  const validRow = (row: unknown): row is number => typeof row === 'number' && Number.isInteger(row) && rows.has(row);
  const rowList = (key: string): number[] => {
    const list = data[key] === undefined ? [] : data[key];
    if (!Array.isArray(list) || list.some(row => !validRow(row)) || new Set(list).size !== list.length) throw new Error('Invalid CFB row indices.');
    return [...list];
  };
  const locked = rowList('locked_rows'), excluded = rowList('excluded_rows');
  const slotLocks = data['slot_locks'] == null ? {} : data['slot_locks'];
  if (typeof slotLocks !== 'object' || Array.isArray(slotLocks)) throw new Error('Invalid CFB slot locks.');
  const slots: Record<string, number> = {};
  for (const [key, row] of Object.entries(slotLocks)) {
    if (!/^(0|[1-9][0-9]*)$/.test(key) || Number(key) >= eligibleRowsBySlot.length ||
      !validRow(row) || !eligibleRowsBySlot[Number(key)].includes(row)) throw new Error('Invalid or ineligible CFB slot lock.');
    slots[key] = row;
  }
  const slotRows = Object.values(slots);
  if (new Set(slotRows).size !== slotRows.length || [...locked, ...slotRows].some(row => excluded.includes(row))) {
    throw new Error('Duplicate slot lock or overlapping CFB lock/exclusion.');
  }
  const minimum = data['min_unique_players'] === undefined ? 1 : data['min_unique_players'];
  if (typeof minimum !== 'number' || !Number.isInteger(minimum) || minimum < 1 || minimum > eligibleRowsBySlot.length) throw new Error('Invalid CFB minimum unique players.');
  return { constraints: { locked_rows: locked, excluded_rows: excluded, slot_locks: slots, min_unique_players: minimum } };
}
